import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Button, FormControl, MenuItem } from '@mui/material';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { AttendanceContext } from '../../context/AttendanceContext';
import ControlledInput from '../forms/ControlledInput';
import ControlledSelect from '../forms/ControlledSelect';
import { AttendanceMarkPayload } from './types';

const Container = styled.div``;

const Title = styled.h1`
    font-size: 20px;
    font-weight: 400;
    margin: 0;
`;

const Date = styled.div``;

const StyledFormControl = styled(FormControl)`
    margin-bottom: 0.5rem;
`;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    margin-bottom: 1rem;
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 1rem;

    button {
        margin-left: 0.5rem;
    }
`;

export interface AttendanceEditFormProps {
    onFinish: (payload: AttendanceMarkPayload) => void;
    onCancel: () => void;
}

const AttendanceEditForm: React.FC<AttendanceEditFormProps> = ({ onCancel, onFinish }) => {
    const { t, students, sessions, selectedStudent, selectedSession, studentId, classSessionId, sessionAttendance, studentAttendance } =
        useContext(AttendanceContext);
    const { control, handleSubmit, watch, setValue } = useForm();

    const student = useMemo(() => students.find((i) => i.student_id === selectedStudent), [students, selectedStudent]);
    const session = useMemo(() => sessions.find((i) => i.session_id === selectedSession), [sessions, selectedSession]);

    const attendance = useMemo(() => {
        if (studentId && studentAttendance) return studentAttendance[selectedSession as string];
        if (classSessionId && sessionAttendance) return sessionAttendance[selectedStudent as string];
    }, [classSessionId, selectedSession, selectedStudent, sessionAttendance, studentAttendance, studentId]);

    const [showAllFields, setShowAllFields] = useState(false);

    const attendanceValue = () => {
        if (!attendance) return 'false';
        if (attendance.marks && attendance.marks.marked_present !== null) return attendance.marks.marked_present.toString();
        if (attendance.events && attendance.events.present) return 'true';

        return 'false';
    };

    const getTardyValue = () => {
        if (!attendance) return 'false';
        if (attendance.marks && attendance.marks.marked_tardy !== null) return attendance.marks.marked_tardy.toString();
        if (attendance.events && attendance.events.tardy) return 'true';

        return 'false';
    };

    const isPresent = watch('present', attendanceValue());

    useEffect(() => {
        const getLeaveTimeValue = () => {
            if (!session) return moment();
            if (!attendance) return moment(session.end_time);
            if (attendance.marks && attendance.marks.marked_leave_time) return moment(attendance.marks.marked_leave_time);
            if (attendance.events && attendance.events.leave_time) return moment(attendance.events.leave_time);

            return moment(session.end_time);
        };

        const getJoinTimeValue = () => {
            if (!session) return moment();
            if (!attendance) return moment(session.start_time);
            if (attendance.marks && attendance.marks.marked_join_time) return moment(attendance.marks.marked_join_time);
            if (attendance.events && attendance.events.join_time) return moment(attendance.events.join_time);

            return moment(session.start_time);
        };

        if (isPresent === 'true') {
            setShowAllFields(true);
            setValue('joinTime', getJoinTimeValue().format('HH:mm'));
            setValue('leaveTime', getLeaveTimeValue().format('HH:mm'));
        } else {
            setShowAllFields(false);
        }
    }, [attendance, isPresent, session, setValue]);

    const onSubmit = (data) => {
        if (!selectedStudent || !selectedSession || !session) return;

        const date = moment(session.start_time);

        const join = moment(data.joinTime, 'HH:mm');
        const leave = moment(data.leaveTime, 'HH:mm');

        if (!join.isValid() || !leave.isValid) return;

        const joinTime = date.clone().set('hour', join.hour()).set('minute', join.minute()).toISOString();
        const leaveTime = date.clone().set('hour', leave.hour()).set('minute', leave.minute()).toISOString();

        const payload: AttendanceMarkPayload = {
            session_id: selectedSession,
            student_ids: [selectedStudent],
            present: data.present === 'true',
            tardy: null,
            join_time: null,
            leave_time: null
        };

        if (payload.present) {
            payload.tardy = data.tardy === 'true';
            payload.join_time = joinTime;
            payload.leave_time = leaveTime;
        }

        onFinish(payload);
        onCancel();
    };

    if (!student || !session) {
        return null;
    }

    return (
        <Container>
            <Header>
                <Title>{t('edit-student-record', { name: student.user_name })}</Title>
                <Date>{moment(session.start_time).format('L - dddd')}</Date>
            </Header>
            <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <StyledFormControl fullWidth>
                    <ControlledSelect defaultValue={attendanceValue()} name={'present'} label={t('attendance')} control={control}>
                        <MenuItem value={'false'}>{t('absent')}</MenuItem>
                        <MenuItem value={'true'}>{t('present')}</MenuItem>
                    </ControlledSelect>
                </StyledFormControl>

                {showAllFields && (
                    <>
                        <StyledFormControl fullWidth>
                            <ControlledSelect defaultValue={getTardyValue()} name={'tardy'} label={t('tardiness')} control={control}>
                                <MenuItem value={'true'}>{t('tardy')}</MenuItem>
                                <MenuItem value={'false'}>{t('not-tardy')}</MenuItem>
                            </ControlledSelect>
                        </StyledFormControl>

                        <StyledFormControl fullWidth>
                            <ControlledInput type="time" name={'joinTime'} label={t('time-in')} control={control} />
                        </StyledFormControl>

                        <StyledFormControl fullWidth>
                            <ControlledInput type="time" name={'leaveTime'} label={t('time-out')} control={control} />
                        </StyledFormControl>
                    </>
                )}

                <Footer>
                    <Button variant={'outlined'} onClick={onCancel}>
                        {t('cancel')}
                    </Button>
                    <Button variant={'contained'} type={'submit'}>
                        {t('save-record')}
                    </Button>
                </Footer>
            </StyledForm>
        </Container>
    );
};

export default AttendanceEditForm;
